import { Accordion, Checkbox, DatePicker, Text, useIptorTranslation, FormControlLabel } from '@iptor/base';
import { Grid2 as Grid } from '@mui/material';
import { Control, Controller, UseFormGetValues } from 'react-hook-form';
import DiscountCardsSection from '../../../components/discounts/DiscountCardsSection';
import { Order } from '../../../constants';
import { formatDate, formatDateToPayloadFormat } from '../../../utils/formatDate';
import { SalesPromotionDropdown, VatHandlingCodesDropdown } from '@iptor/business';
import { DisplayRulesKey } from '../constants/displayRules';
import dayjs from 'dayjs';

type CurrencyAndTaxProps = {
  getValues?: UseFormGetValues<Partial<Order>>;
  control?: Control<Partial<Order>>;
  order?: number;
  orderCurrency: string;
  isReadOnlyFromDisplayRulesByKey: (key: DisplayRulesKey) => boolean;
  onDiscountChange: () => void;
};

const CurrencyAndTax = ({
  control,
  getValues,
  order,
  isReadOnlyFromDisplayRulesByKey,
  orderCurrency,
  onDiscountChange,
}: CurrencyAndTaxProps) => {
  const { t } = useIptorTranslation();
  return (
    <Accordion
      headingTitle={t('common:TXT_Currency_And_Tax')}
      moreInfo={
        <Grid container columnSpacing={3} rowSpacing={1} alignItems="flex-end">
          <Grid size={3}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', margin: 0 }}
              control={
                <Text id="primaryCurrency" fontSize={14} color="textDisabled">
                  {getValues?.('primaryCurrency') || ' '}
                </Text>
              }
              label={<Text fontWeight={600}>{t('common:TXT_Currency')}</Text>}
              labelPlacement="top"
            />
          </Grid>
          <Grid size={3}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', margin: 0 }}
              control={
                <Text id="priceList" fontSize={14} color="textDisabled">
                  {getValues?.('priceList') || ' '}
                </Text>
              }
              label={<Text fontWeight={600}>{t('common:TXT_Price_List')}</Text>}
              labelPlacement="top"
            />
          </Grid>
          <Grid size={3}>
            <Controller
              name="backlogAfterPickListConf"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox id={field.name} {...field} />}
                  label={t('common:TXT_Backlog_At_Pick')}
                  readOnly={isReadOnlyFromDisplayRulesByKey('backlogAfterPickListConf')}
                />
              )}
            />
          </Grid>
        </Grid>
      }
    >
      <Grid container columnSpacing={3} rowSpacing={1}>
        <Grid size={12 / 4}>
          <Controller
            name="vatHandlingCode"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <VatHandlingCodesDropdown
                value={field.value}
                onChange={(value) => field.onChange(value)}
                id="vatHandlingCode"
                fullWidth
                color={error ? 'error' : 'primary'}
                helperText={error ? error.message : ''}
                label={t('common:TXT_VAT_Handling_Codes')}
                readOnly={isReadOnlyFromDisplayRulesByKey('vatHandlingCodes')}
              />
            )}
          />
        </Grid>

        <Grid size={12 / 4}>
          <Controller
            name="promotionCode"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <SalesPromotionDropdown
                onChange={(value) => field.onChange(value)}
                id="promotionCode"
                value={field.value}
                color={error ? 'error' : 'primary'}
                helperText={error ? error.message : ''}
                fullWidth
                label={t('common:TXT_Promotion')}
                readOnly={isReadOnlyFromDisplayRulesByKey('promotionCode')}
              />
            )}
          />
        </Grid>

        <Grid size={12 / 2}>
          <Controller
            name="pricingDate"
            control={control}
            rules={{
              validate: (value) => !value || dayjs(value).isValid() || 'Invalid date format',
            }}
            render={({ field }) => (
              <DatePicker
                label={t('common:TXT_Pricing_Date')}
                value={formatDate(getValues?.('pricingDate'))}
                readOnly={isReadOnlyFromDisplayRulesByKey('pricingDate')}
                slotProps={{
                  textField: {
                    id: 'pricingDate',
                  },
                }}
                onChange={(value) => field.onChange(formatDateToPayloadFormat(value))}
              />
            )}
          />
        </Grid>

        <Grid size={12} sx={{ marginTop: 3 }}>
          <Controller
            name="vat"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox id="vat" {...field} checked={!!field.value} />}
                label={t('common:TXT_VAT_Based')}
                readOnly={isReadOnlyFromDisplayRulesByKey('vat')}
              />
            )}
          />
        </Grid>
        {order && (
          <DiscountCardsSection
            order={order}
            orderCurrency={orderCurrency}
            readOnly={isReadOnlyFromDisplayRulesByKey('discounts')}
            onDiscountChange={() => {
              onDiscountChange();
            }}
          />
        )}
      </Grid>
    </Accordion>
  );
};

export default CurrencyAndTax;
